import { useState } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { ConfirmModal } from '../common/Modal';
import { useProjects } from '../../hooks/useProjects';
import {
  useChannelProjectLinks,
  useCreateChannelProjectLink,
  useDeleteChannelProjectLink,
} from '../../hooks/useBotAdmin';
import type { ChannelProjectLink } from '../../services/api/botAdmin';

/**
 * Phase 79 (Plan 79-02 / D-2) — admin surface to bind a Slack channel to one
 * project. This is the ONLY writer of the rows channel task filing resolves
 * against server-side (79-01). A channelId is UNIQUE, so a duplicate is a
 * server-side ConflictError surfaced inline here (never a silent overwrite).
 *
 * Structural sibling of `ChannelClaudeTokenField.tsx` — same card shell,
 * server-confirm-only mutations, no optimistic update.
 */
export function ChannelProjectLinks() {
  const { data: links, isLoading, error } = useChannelProjectLinks();
  const { data: projects } = useProjects();
  const createMutation = useCreateChannelProjectLink();
  const deleteMutation = useDeleteChannelProjectLink();

  const [channelName, setChannelName] = useState('');
  const [channelId, setChannelId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [pendingDelete, setPendingDelete] = useState<ChannelProjectLink | null>(null);

  const rows = links ?? [];
  // Only active (in_progress) projects, sorted by name — a channel binds to a
  // live project, and the full unsorted list is unwieldy in a picker.
  const projectOptions = (projects ?? [])
    .filter((p) => p.state === 'in_progress')
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedId = channelId.trim();
    if (!trimmedId || !projectId) return;
    createMutation.mutate(
      { channelId: trimmedId, projectId: Number(projectId), channelName: channelName.trim() || undefined },
      {
        onSuccess: () => {
          setChannelName('');
          setChannelId('');
          setProjectId('');
        },
      }
    );
  };

  const closeDeleteModal = () => {
    if (deleteMutation.isPending) return;
    setPendingDelete(null);
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.id, { onSuccess: () => setPendingDelete(null) });
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <p className="text-sm font-semibold text-gray-900">Channel → project links</p>
      <p className="text-sm text-gray-500 mt-0.5">
        Bind a Slack channel to one project. When Monk files a task from that channel, it lands on
        the linked project (attributed to the requesting user).
      </p>

      <form onSubmit={handleCreate} className="mt-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[10rem]">
            <Input
              id="channel-project-channel-name"
              label="Channel name"
              autoComplete="off"
              placeholder="#engineering"
              value={channelName}
              onChange={(e) => setChannelName(e.target.value)}
            />
          </div>
          <div className="flex-1 min-w-[10rem]">
            <Input
              id="channel-project-channel-id"
              label="Channel ID"
              autoComplete="off"
              placeholder="C0123ABCDE"
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
            />
          </div>
          <div className="flex-1 min-w-[14rem]">
            <label
              htmlFor="channel-project-project"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Project
            </label>
            <select
              id="channel-project-project"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select a project…</option>
              {projectOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.slug})
                </option>
              ))}
            </select>
          </div>
          <Button
            type="submit"
            disabled={!channelId.trim() || !projectId || createMutation.isPending}
            isLoading={createMutation.isPending}
          >
            Link
          </Button>
        </div>
        <p className="mt-1.5 text-xs text-gray-500">
          <span className="font-medium text-gray-600">Channel ID</span> is what Monk matches on —
          in Slack, open the channel details and copy the ID.
        </p>
      </form>

      {createMutation.isError && (
        <p className="mt-2 text-sm text-red-600">
          {(createMutation.error as Error).message}
        </p>
      )}

      <div className="mt-4">
        {isLoading ? (
          <p className="text-sm text-gray-400">Loading links…</p>
        ) : error ? (
          <p className="text-sm text-red-600">Failed to load channel links.</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500">No channels linked yet.</p>
        ) : (
          <div className="overflow-auto rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                    Channel
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                    Project
                  </th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.map((row, idx) => (
                  <tr key={row.id} className={idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-4 py-2 text-sm whitespace-nowrap">
                      {row.channelName ? (
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900">{row.channelName}</span>
                          <span className="font-mono text-xs text-gray-400">{row.channelId}</span>
                        </div>
                      ) : (
                        <span className="font-mono text-gray-900">{row.channelId}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                      {row.projectName ?? `#${row.projectId}`}
                      {row.projectSlug ? (
                        <span className="text-xs text-gray-400"> ({row.projectSlug})</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => setPendingDelete(row)}
                        aria-label={`Unlink ${row.channelId}`}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pendingDelete && (
        <ConfirmModal
          isOpen={true}
          onClose={closeDeleteModal}
          onConfirm={confirmDelete}
          title={`Unlink ${pendingDelete.channelId}?`}
          message="Monk will no longer file tasks from this channel until it is linked again."
          confirmText="Unlink"
          cancelText="Cancel"
          variant="danger"
          isLoading={deleteMutation.isPending}
          error={deleteMutation.isError ? (deleteMutation.error as Error).message : null}
        />
      )}
    </div>
  );
}
